/**
 * Hook for image generation.
 *
 * Uses the streaming WebSocket endpoint so progress + partial frames flow into
 * the studio-ai store as the image renders. Wraps the WS lifecycle in a Promise
 * so callers (the wizard, etc.) keep their `await mutateAsync(...)` contract —
 * the resolved value is the final ImageGenerationResult.
 */

import { useMutation } from "@tanstack/react-query"
import { toast } from "sonner"

import type {
  ImageGenerationRequest,
  ImageGenerationResult,
  VideoStyle,
} from "../types/studio-ai.types"

import { connectImageGenerationWebSocket } from "@/api/studio-ai-api"
import i18n from "@/languageConfig"
import { useStudioAIStore } from "@/stores/studio-ai-store"

import { ImageQuality, ImageSize, MediaType } from "../types/studio-ai.types"

export function useGenerateImage() {
  const updateGenerationState = useStudioAIStore((state) => state.updateGenerationState)
  const resetGenerationState = useStudioAIStore((state) => state.resetGenerationState)

  return useMutation({
    mutationFn: async (request: ImageGenerationRequest): Promise<ImageGenerationResult> => {
      updateGenerationState({
        isGenerating: true,
        status: "starting",
        progress: 0,
        partialImageUrl: null,
        // Explicitly clear any leftover job_id from a previous run so consumers
        // keying off currentJobId don't see a stale value during this run.
        currentJobId: null,
        estimatedTime: undefined,
      })

      return new Promise<ImageGenerationResult>((resolve, reject) => {
        let settled = false
        let ws: WebSocket | null = null
        const finish = (action: () => void, closeNow = true) => {
          if (settled) return
          settled = true
          if (closeNow) {
            // Close the socket explicitly so we don't leak it on the JS heap
            // when the server doesn't initiate close (timeouts, hangs, errors).
            try {
              ws?.close()
            } catch {
              // ignore — close on an already-closed/closing socket is a no-op
            }
          } else if (ws) {
            // Success path: let the SERVER close the socket. Closing first races
            // the backend's own close — the disconnected client makes quart's
            // close-send throw before it sets `_closed`, so quart re-closes on
            // teardown and uvicorn logs an "Unexpected ASGI message
            // 'websocket.close'" error. Fallback-close so a hung server can't
            // leak the socket.
            const socket = ws
            const fallback = setTimeout(() => {
              try {
                socket.close()
              } catch {
                // ignore
              }
            }, 3000)
            socket.addEventListener("close", () => clearTimeout(fallback), { once: true })
          }
          action()
        }

        ws = connectImageGenerationWebSocket({
          request,
          onStarted: (jobId) => {
            updateGenerationState({
              currentJobId: jobId ?? null,
              status: "starting",
              progress: 0,
            })
          },
          onProgress: (progress, data) => {
            const eventJobId =
              data && typeof data === "object" && "job_id" in data
                ? String((data as { job_id?: unknown }).job_id ?? "")
                : ""
            updateGenerationState({
              progress,
              status: progress > 0 ? "generating" : "starting",
              currentJobId: eventJobId || useStudioAIStore.getState().generationState.currentJobId,
            })
          },
          onPartialImage: (imageUrl, progress) => {
            updateGenerationState({
              progress,
              status: "generating",
              partialImageUrl: imageUrl,
            })
          },
          onComplete: (result) => {
            // Keep the final image visible in the store so consumers can show
            // it before navigating away. resetGenerationState() will clear it.
            updateGenerationState({
              progress: 100,
              status: "completed",
              partialImageUrl: result.image_url ?? null,
            })
            // The WS image_complete event omits request-shaped fields (prompt,
            // size, quality, etc.) — fold them in from the original request so
            // onSuccess + the gallery cache get a fully-populated row.
            const enriched: ImageGenerationResult = {
              ...result,
              prompt: result.prompt || request.prompt,
              original_prompt: result.original_prompt || request.prompt,
              size: result.size || request.size || ImageSize.Square1024,
              quality: result.quality || request.quality || ImageQuality.Medium,
              style: result.style || request.style,
            }
            // closeNow=false: let the backend close the socket (see finish()).
            finish(() => resolve(enriched), false)
          },
          onError: (errorMessage) => {
            resetGenerationState()
            finish(() => reject(new Error(errorMessage)))
          },
          onActivity: (message) => {
            updateGenerationState({ status: message })
          },
        })
      })
    },
    onSuccess: (result: ImageGenerationResult) => {
      // Cache all generated images in store + localStorage so they're visible on landing
      const addImage = useStudioAIStore.getState().addImage
      const images = result.images ?? [
        {
          job_id: result.job_id,
          image_url: result.image_url,
          revised_prompt: result.revised_prompt,
        },
      ]

      for (const img of images) {
        addImage({
          id: img.job_id,
          job_id: img.job_id,
          prompt: result.prompt || "",
          original_prompt: result.original_prompt,
          image_url: img.image_url,
          status: result.status,
          size: result.size || "1024x1024",
          quality: (result.quality || "high") as ImageQuality,
          style: result.style as VideoStyle | undefined,
          revised_prompt: img.revised_prompt,
          created_at: result.created_at || new Date().toISOString(),
          media_type: MediaType.Image,
        })
      }

      toast.success(i18n.t("studioAi.generationForm.imageSuccess"), {
        description: result.revised_prompt
          ? i18n.t("studioAi.generationForm.imageSuccessEnhanced")
          : undefined,
      })

      updateGenerationState({
        isGenerating: false,
        currentJobId: result.job_id,
        status: "completed",
        progress: 100,
      })
    },
    onError: (error: Error) => {
      if (!error.message.toLowerCase().includes("style template not found or access denied")) {
        toast.error(i18n.t("studioAi.generationForm.failedToGenerate"), {
          description: error.message,
        })
      }

      resetGenerationState()
    },
  })
}
